import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import IgnoreIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import LanIcon from "@mui/icons-material/Lan";
import ComputerIcon from "@mui/icons-material/Computer";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PrintIcon from "@mui/icons-material/Print";
import RouterIcon from "@mui/icons-material/Router";
import LaptopIcon from "@mui/icons-material/Laptop";
import StorageIcon from "@mui/icons-material/Storage";
import { useNavigate } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { NetworkScansAPI } from "../api/networkScans";
import { EnterprisesAPI } from "../api/enterprises";
import { ROUTES } from "../routes";
import PremiumFeatureGate from "../components/PremiumFeatureGate";
import { useAuth } from "../providers/AuthProvider";

const INITIAL_FORM = {
  display_name: "",
  assigned_to: "",
  department: "",
  location: "",
  serial_number: "",
  notes: "",
};

function StatCard({ label, value, hint, color = "#0f172a" }) {
  return (
    <Paper
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #dbe5f1",
        bgcolor: "white",
        minHeight: 112,
        boxShadow: "0 10px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, color, lineHeight: 1 }}>
          {value}
        </Typography>
        {hint ? (
          <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.45 }}>
            {hint}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

function isPrivateIp(ip) {
  const parts = String(ip || "").split(".").map(Number);
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) return false;
  const [a, b] = parts;
  if (a === 10) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  if (a === 192 && b === 168) return true;
  return false;
}

function ipToSubnet24(ip) {
  const parts = String(ip || "").split(".").map((part) => Number(part));
  if (parts.length !== 4 || parts.some((part) => !Number.isFinite(part))) return "";
  return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
}

async function detectLocalPrivateIp() {
  if (typeof window === "undefined" || typeof RTCPeerConnection === "undefined") return "";

  return new Promise((resolve) => {
    let resolved = false;
    const finish = (value = "") => {
      if (resolved) return;
      resolved = true;
      resolve(value);
    };

    try {
      const peer = new RTCPeerConnection({ iceServers: [] });
      const candidates = new Set();

      peer.createDataChannel("scan");
      peer.onicecandidate = (event) => {
        const candidate = String(event?.candidate?.candidate || "");
        if (!candidate) {
          finish(Array.from(candidates).find(Boolean) || "");
          return;
        }

        const matches = candidate.match(/(\d{1,3}(?:\.\d{1,3}){3})/g) || [];
        matches.forEach((ip) => {
          if (isPrivateIp(ip)) candidates.add(ip);
        });
      };

      peer.createOffer()
        .then((offer) => peer.setLocalDescription(offer))
        .catch(() => finish(""));

      setTimeout(() => finish(Array.from(candidates).find(Boolean) || ""), 1800);
    } catch {
      finish("");
    }
  });
}

function getDeviceIcon(type, hostname = "") {
  const value = `${type || ""} ${hostname || ""}`.toLowerCase();
  if (value.includes("printer")) return <PrintIcon fontSize="small" />;
  if (value.includes("router") || value.includes("switch") || value.includes("ap")) return <RouterIcon fontSize="small" />;
  if (value.includes("phone") || value.includes("mobile") || value.includes("android")) return <PhoneAndroidIcon fontSize="small" />;
  if (value.includes("server")) return <StorageIcon fontSize="small" />;
  if (value.includes("laptop")) return <LaptopIcon fontSize="small" />;
  return <ComputerIcon fontSize="small" />;
}

function deviceBadgeColor(status) {
  const value = String(status || "pending").toLowerCase();
  if (value === "added") return { bg: "#dcfce7", fg: "#166534", label: "Synced" };
  if (value === "ignored") return { bg: "#e2e8f0", fg: "#475569", label: "Ignored" };
  return { bg: "#dbeafe", fg: "#1d4ed8", label: "Pending" };
}

function stageBadge(stage) {
  const value = String(stage || "discovered").toLowerCase();
  if (value === "identified") return { bg: "#dcfce7", fg: "#166534", label: "Identified" };
  if (value === "fingerprinted") return { bg: "#dbeafe", fg: "#1d4ed8", label: "Fingerprinted" };
  if (value === "added") return { bg: "#e0f2fe", fg: "#0369a1", label: "Added" };
  if (value === "ignored") return { bg: "#e2e8f0", fg: "#475569", label: "Ignored" };
  return { bg: "#f1f5f9", fg: "#334155", label: "Discovered" };
}

function connectorBadge(status) {
  const value = String(status || "").toLowerCase();
  if (value === "ready") return { bg: "#dcfce7", fg: "#166534", label: "Connector ready" };
  if (value === "unreachable") return { bg: "#fee2e2", fg: "#991b1b", label: "Connector unreachable" };
  if (value === "no-credential") return { bg: "#f1f5f9", fg: "#334155", label: "No credential" };
  return { bg: "#e2e8f0", fg: "#475569", label: "Pending" };
}

function rowSx(status) {
  const value = String(status || "pending").toLowerCase();
  if (value === "added") return { bgcolor: "#f0fdf4" };
  if (value === "ignored") return { bgcolor: "#f8fafc", opacity: 0.72 };
  return { bgcolor: "white" };
}

function buildFormFromDevice(device) {
  return {
    display_name: device?.hostname || device?.ip_address || "",
    assigned_to: "",
    department: "",
    location: "",
    serial_number: "",
    notes: device?.review_notes || device?.raw_data?.sync_notes || "",
  };
}

function toDevicePayload(form, device) {
  return {
    display_name: String(form.display_name || device?.hostname || device?.ip_address || "Discovered device").trim(),
    assigned_to: String(form.assigned_to || "").trim() || null,
    department: String(form.department || "").trim() || null,
    location: String(form.location || "").trim() || null,
    serial_number: String(form.serial_number || "").trim() || null,
    notes: String(form.notes || "").trim() || null,
  };
}

export default function NetworkScanPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scan, setScan] = useState(null);
  const [localSubnet, setLocalSubnet] = useState("");
  const [deviceTypeFilter, setDeviceTypeFilter] = useState("ALL");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add");
  const [activeDevice, setActiveDevice] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving] = useState(false);
  const [cliDialogOpen, setCliDialogOpen] = useState(false);
  const [agentToken, setAgentToken] = useState("");
  const [tokenLoading, setTokenLoading] = useState(true);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = useCallback((message, severity = "success") => {
    setToast({ open: true, message, severity });
  }, []);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await NetworkScansAPI.latest();
      setScan(res?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load network scan");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLatest();
  }, [loadLatest]);

  useEffect(() => {
    let mounted = true;
    void detectLocalPrivateIp().then((ip) => {
      if (!mounted) return;
      setLocalSubnet(ip ? ipToSubnet24(ip) : "");
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setTokenLoading(true);
    if (auth?.user?.role === "enterprise_admin") {
      EnterprisesAPI.getMyToken()
        .then((res) => {
          if (!mounted) return;
          setAgentToken(String(res?.data?.token || "").trim());
        })
        .catch(() => {
          if (!mounted) return;
          setAgentToken("");
        })
        .finally(() => {
          if (mounted) setTokenLoading(false);
        });
    } else {
      setAgentToken("");
      setTokenLoading(false);
    }
    return () => {
      mounted = false;
    };
  }, [auth?.user?.role]);

  const devices = useMemo(() => scan?.devices || [], [scan]);
  const deviceTypeOptions = useMemo(() => {
    const set = new Set(["ALL"]);
    devices.forEach((device) => {
      if (device.device_type) set.add(device.device_type);
    });
    return Array.from(set);
  }, [devices]);

  const filteredDevices = useMemo(() => {
    return devices.filter((device) => {
      if (deviceTypeFilter !== "ALL" && String(device.device_type || "") !== deviceTypeFilter) return false;
      if (stageFilter !== "ALL" && String(device.scan_stage || "discovered") !== stageFilter) return false;
      if (statusFilter !== "ALL" && String(device.status || "pending") !== statusFilter) return false;
      return true;
    });
  }, [deviceTypeFilter, devices, stageFilter, statusFilter]);

  useEffect(() => {
    setSelectedDeviceIds((current) => current.filter((deviceId) => filteredDevices.some((device) => device.id === deviceId)));
  }, [filteredDevices]);

  const summary = useMemo(() => {
    const totals = devices.reduce(
      (acc, device) => {
        acc.found += 1;
        if (device.status === "added") acc.added += 1;
        else if (device.status === "ignored") acc.ignored += 1;
        else acc.pending += 1;
        if (device.is_online) acc.online += 1;
        else acc.offline += 1;
        if (device.scan_stage === "identified") acc.identified += 1;
        else if (device.scan_stage === "fingerprinted") acc.fingerprinted += 1;
        else acc.discovered += 1;
        return acc;
      },
      { found: 0, added: 0, ignored: 0, pending: 0, online: 0, offline: 0, discovered: 0, fingerprinted: 0, identified: 0 }
    );
    return totals;
  }, [devices]);

  const pendingDevices = useMemo(
    () => devices.filter((device) => String(device.status || "pending").toLowerCase() === "pending"),
    [devices]
  );

  const allVisibleSelected = filteredDevices.length > 0 && filteredDevices.every((device) => selectedDeviceIds.includes(device.id));
  const anySelected = selectedDeviceIds.length > 0;

  const updateDeviceState = useCallback(async (device, status) => {
    if (!scan?.id || !device?.id) return;
    try {
      const res = await NetworkScansAPI.updateStatus(scan.id, device.id, { status });
      setScan(res?.data?.data || null);
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to update device", "error");
    }
  }, [scan?.id, showToast]);

  const bulkUpdateDevices = useCallback(async (deviceIds, status) => {
    if (!scan?.id || !deviceIds?.length) return;
    try {
      const res = await NetworkScansAPI.bulkStatus(scan.id, { device_ids: deviceIds, status });
      setScan(res?.data?.data || null);
      setSelectedDeviceIds([]);
      showToast(`${deviceIds.length} device${deviceIds.length > 1 ? "s" : ""} updated.`, "success");
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to update devices", "error");
    }
  }, [scan?.id, showToast]);

  const ignoreSelected = useCallback(() => {
    void bulkUpdateDevices(selectedDeviceIds, "ignored");
  }, [bulkUpdateDevices, selectedDeviceIds]);

  const ignoreAllPending = useCallback(() => {
    void bulkUpdateDevices(pendingDevices.map((device) => device.id), "ignored");
  }, [bulkUpdateDevices, pendingDevices]);

  const clearScan = useCallback(async () => {
    if (!scan?.id) return;
    if (!window.confirm("Delete this scan and all discovered rows? This does not delete devices that were already synced into Monitor.")) return;
    try {
      await NetworkScansAPI.delete(scan.id);
      setScan(null);
      setSelectedDeviceIds([]);
      showToast("Network scan deleted.", "success");
      await loadLatest();
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to delete scan", "error");
    }
  }, [loadLatest, scan?.id, showToast]);

  const openDrawer = useCallback((device, mode = "add") => {
    setActiveDevice(device);
    setDrawerMode(mode);
    setForm(buildFormFromDevice(device));
    setDrawerOpen(true);
  }, []);

  const saveDrawer = useCallback(async () => {
    if (!scan?.id || !activeDevice?.id) return;
    try {
      const res = await NetworkScansAPI.addToMonitor(scan.id, activeDevice.id, toDevicePayload(form, activeDevice));
      setScan(res?.data?.data || scan);
      showToast(drawerMode === "edit" ? "Monitor device updated." : "Device synced to Monitor.", "success");
      setDrawerOpen(false);
      await loadLatest();
    } catch (err) {
      showToast(err?.response?.data?.error || "Failed to sync device", "error");
    }
  }, [activeDevice, drawerMode, form, loadLatest, scan, showToast]);

  const pendingCount = summary.pending;
  const cliApiUrl = "http://178.236.185.146:4000/api";
  const cliTarget = scan?.target_cidr || localSubnet || "192.168.0.0/24";
  const cliCommand = agentToken
    ? `node agentless-scan-cli.mjs --api-url ${cliApiUrl} --token ${agentToken} --target ${cliTarget} --max-hosts 254`
    : `node agentless-scan-cli.mjs --api-url ${cliApiUrl} --token <agent-token> --target ${cliTarget} --max-hosts 254`;
  const cliWindowsCommand = agentToken
    ? `agentless-scan-cli.cmd --api-url ${cliApiUrl} --token ${agentToken} --target ${cliTarget} --max-hosts 254`
    : `agentless-scan-cli.cmd --api-url ${cliApiUrl} --token <agent-token> --target ${cliTarget} --max-hosts 254`;
  const copyCliCommand = async () => {
    try {
      await navigator.clipboard.writeText(cliCommand);
      showToast("Run command copied.", "success");
    } catch {
      showToast("Could not copy the command.", "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PremiumFeatureGate
      title="Agentless Scan"
      description="Review a network range, discover connected devices, and promote approved records into Monitor."
    >
    <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: { xs: 1.5, md: 2.5 } }}>
      <Paper sx={{ p: 2.5, border: "1px solid #dbe6f3", borderRadius: 2, mb: 2, background: "linear-gradient(135deg, #eff6ff 0%, #ffffff 60%)" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900 }}>
              Agentless Scan
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
              Agentless discovery for a defined network range. Enter the site or VLAN subnet you want to review, then sync discovered devices into Monitor or ignore them.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => void loadLatest()}
              sx={{ minWidth: 116, height: 44, px: 2 }}
            >
              Refresh
            </Button>
            {auth?.user?.role === "enterprise_admin" ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setCliDialogOpen(true)}
                sx={{ minWidth: 150, height: 44, px: 2 }}
              >
                Download CLI
              </Button>
            ) : null}
          </Stack>
        </Stack>
        {error ? <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert> : null}
        {localSubnet ? (
          <Alert
            severity={scan?.target_cidr === localSubnet ? "success" : "warning"}
            sx={{ mt: 1.5 }}
          >
            Your browser appears to be on {localSubnet}. Use this as the CLI target if it matches the office or site network.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            We could not infer the local subnet automatically. If you do not know the target, check your device IP and gateway with ipconfig or the router/DHCP settings.
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2, mb: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Production discovery runs from the CLI on a machine inside the customer network or VPN. This page is used to download the scanner, review uploads, and manage discovered devices.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The CLI target should be the site or VLAN subnet, such as `192.168.0.0/24`, not the gateway IP itself.
          </Typography>
          <Alert severity="info">
            Agentless scan shows only what the network exposes: live hosts, IPs, MAC addresses from the ARP cache, and connector-backed identification when available. Synced devices are written into Monitor and deduped by MAC first, then IP.
          </Alert>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe5f1", bgcolor: "white" }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>Discover</Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>{summary.online}</Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>Hosts checked within the configured limit.</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe5f1", bgcolor: "white" }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>Fingerprint</Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>{summary.fingerprinted}</Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>Live hosts without an open connector.</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe5f1", bgcolor: "white" }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>Identify</Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>{summary.identified}</Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>Matched via WMI, SSH, or SNMP.</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe5f1", bgcolor: "white" }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>Review</Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>{summary.pending}</Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>Sync to Monitor or ignore.</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe5f1", bgcolor: "white", minHeight: 116 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>
              CLI last run
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.75 }}>
              {scan?.completed_at ? new Date(scan.completed_at).toLocaleString() : "No CLI sync yet"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              {scan?.summary?.scanner_name ? `Scanner node: ${scan.summary.scanner_name}` : "No scanner node connected yet."}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe5f1", bgcolor: "white", minHeight: 116 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>
              Scanner status
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.75 }}>
              {scan?.summary?.source === "agentless-cli" ? "Online" : "Waiting"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              {scan?.summary?.scanner_platform ? `${scan.summary.scanner_platform} • ${scan.summary.scanner_user || "unknown user"}` : "No heartbeat yet from the CLI."}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe5f1", bgcolor: "white", minHeight: 116 }}>
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800, textTransform: "uppercase" }}>
              CLI target
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.75 }}>
              {scan?.target_cidr || "Not set"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              {scan?.scan_label || "Latest uploaded scan label."}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}><StatCard label="Targets" value={summary.found} hint="Addresses probed from the CIDR" /></Grid>
        <Grid item xs={6} md={2}><StatCard label="Synced" value={summary.added} color="#166534" hint="Written into Monitor device records" /></Grid>
        <Grid item xs={6} md={2}><StatCard label="Ignored" value={summary.ignored} color="#475569" hint="Excluded from follow-up" /></Grid>
        <Grid item xs={6} md={2}><StatCard label="Pending" value={pendingCount} color="#1d4ed8" hint="Still waiting for review" /></Grid>
        <Grid item xs={6} md={2}><StatCard label="Responsive" value={summary.online} color="#0f766e" hint="Live hosts detected on the network" /></Grid>
        <Grid item xs={6} md={2}><StatCard label="Silent" value={summary.offline} color="#b45309" hint="No response detected during scan" /></Grid>
      </Grid>

      <Paper sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Device type</InputLabel>
            <Select label="Device type" value={deviceTypeFilter} onChange={(e) => setDeviceTypeFilter(e.target.value)}>
              {deviceTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === "ALL" ? "All device types" : option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Pipeline stage</InputLabel>
            <Select label="Pipeline stage" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
              <MenuItem value="ALL">All stages</MenuItem>
              <MenuItem value="discovered">Discovered</MenuItem>
              <MenuItem value="fingerprinted">Fingerprinted</MenuItem>
              <MenuItem value="identified">Identified</MenuItem>
              <MenuItem value="added">Added</MenuItem>
              <MenuItem value="ignored">Ignored</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="ALL">All statuses</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="added">Added</MenuItem>
              <MenuItem value="ignored">Ignored</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" label={scan?.target_cidr || "No scan yet"} variant="outlined" sx={{ bgcolor: "#f8fafc" }} />
          <Chip size="small" label={scan?.scan_label || "Untitled scan"} variant="outlined" sx={{ bgcolor: "#f8fafc" }} />
          {scan?.id ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" onClick={ignoreAllPending} disabled={pendingDevices.length === 0}>
                Ignore all
              </Button>
              <Button size="small" variant="outlined" onClick={ignoreSelected} disabled={!anySelected}>
                Ignore selected
              </Button>
              <Button size="small" color="error" variant="text" onClick={() => void clearScan()}>
                Delete scan
              </Button>
            </Stack>
          ) : null}
        </Stack>

        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
            <Box component="thead">
              <Box component="tr" sx={{ "& th": { textAlign: "left", px: 1.75, py: 1.25, fontSize: 12, color: "#64748b", borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc", fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.3 } }}>
                <Box component="th" sx={{ borderTopLeftRadius: 12 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSelectedDeviceIds(checked ? filteredDevices.map((device) => device.id) : []);
                    }}
                    aria-label="Select all visible devices"
                    style={{ width: 16, height: 16, verticalAlign: "middle" }}
                  />
                </Box>
                <Box component="th">Device</Box>
                <Box component="th">IP</Box>
                <Box component="th">Stage</Box>
                <Box component="th">MAC</Box>
                <Box component="th">Type</Box>
                <Box component="th">Status</Box>
                <Box component="th" sx={{ borderTopRightRadius: 12 }}>Review</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {filteredDevices.length === 0 ? (
                <Box component="tr">
                  <Box component="td" colSpan={8} sx={{ py: 6, textAlign: "center", color: "#64748b", bgcolor: "#fcfdff" }}>
                    No discovered devices match the current filters.
                    <Box sx={{ mt: 0.75, color: "#94a3b8" }}>
                      {scan ? "If you expected devices here, verify the subnet or scan connectors and try again." : "Run a scan to populate this queue."}
                    </Box>
                  </Box>
                </Box>
              ) : filteredDevices.map((device, index) => {
                const badge = deviceBadgeColor(device.status);
                const pipelineBadge = stageBadge(device.scan_stage);
                const isLast = index === filteredDevices.length - 1;
                return (
                  <Box
                    component="tr"
                    key={device.id}
                    sx={{
                      ...rowSx(device.status),
                      "& td": {
                        px: 1.75,
                        py: 1.35,
                        borderBottom: isLast ? "none" : "1px solid #edf2f7",
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    <Box component="td">
                      <input
                        type="checkbox"
                        checked={selectedDeviceIds.includes(device.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedDeviceIds((current) =>
                            checked
                              ? Array.from(new Set([...current, device.id]))
                              : current.filter((id) => id !== device.id)
                          );
                        }}
                        aria-label={`Select ${device.hostname || device.ip_address}`}
                        style={{ width: 16, height: 16 }}
                      />
                    </Box>
                    <Box component="td">
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{ width: 34, height: 34, borderRadius: 2, bgcolor: "#eff6ff", color: "#1d4ed8", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
                          {getDeviceIcon(device.device_type, device.hostname)}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {device.hostname || device.ip_address}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {device.is_online ? "Live host detected during scan" : "No response detected"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                    <Box component="td" sx={{ fontFamily: "monospace" }}>{device.ip_address || "-"}</Box>
                    <Box component="td">
                      <Chip size="small" label={pipelineBadge.label} sx={{ bgcolor: pipelineBadge.bg, color: pipelineBadge.fg, fontWeight: 700 }} />
                    </Box>
                    <Box component="td" sx={{ fontFamily: "monospace" }}>{device.mac_address || "-"}</Box>
                    <Box component="td">{device.device_type || "Unknown"}</Box>
                    <Box component="td">
                      <Chip
                        size="small"
                        icon={<LanIcon />}
                        label={device.is_online ? "Online" : "Offline"}
                        sx={{
                          bgcolor: device.is_online ? "#dcfce7" : "#fee2e2",
                          color: device.is_online ? "#166534" : "#991b1b",
                          fontWeight: 700,
                          border: "1px solid transparent",
                        }}
                      />
                    </Box>
                    <Box component="td">
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={badge.label} sx={{ bgcolor: badge.bg, color: badge.fg, fontWeight: 700 }} />
                        {device.status === "pending" ? (
                          <>
                            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => openDrawer(device, "add")}>
                              Sync to Monitor
                            </Button>
                            <Button size="small" color="inherit" startIcon={<IgnoreIcon />} onClick={() => void updateDeviceState(device, "ignored")}>
                              Ignore
                            </Button>
                          </>
                        ) : device.status === "ignored" ? (
                          <Button size="small" color="inherit" startIcon={<RefreshIcon />} onClick={() => void updateDeviceState(device, "pending")}>
                            Undo
                          </Button>
                        ) : (
                          <>
                            <Tooltip title="Edit monitor device">
                              <IconButton size="small" color="primary" onClick={() => openDrawer(device, "edit")}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Button size="small" variant="text" onClick={() => navigate(ROUTES.MONITOR)}>
                              View in Monitor
                            </Button>
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ slotProps: { backdrop: { sx: { top: { xs: 64, sm: 70 } } } } }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 540, md: 640 },
            p: 2,
            bgcolor: "#f8fafc",
            top: { xs: 64, sm: 70 },
            height: { xs: "calc(100% - 64px)", sm: "calc(100% - 70px)" },
          },
        }}
      >
        <Stack spacing={2} sx={{ height: "100%" }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {drawerMode === "edit" ? "Edit monitor device" : "Sync discovered device to Monitor"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The discovered network fields are read-only. Complete the device details, then save it into Monitor.
            </Typography>
          </Box>

          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0", bgcolor: "white" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Discovered data</Typography>
            <Stack spacing={0.75}>
              <Typography variant="body2"><strong>Hostname:</strong> {activeDevice?.hostname || "-"}</Typography>
              <Typography variant="body2"><strong>IP:</strong> {activeDevice?.ip_address || "-"}</Typography>
              <Typography variant="body2"><strong>MAC:</strong> {activeDevice?.mac_address || "-"}</Typography>
              <Typography variant="body2"><strong>Type:</strong> {activeDevice?.device_type || "Unknown"}</Typography>
              <Typography variant="body2"><strong>OS fingerprint:</strong> {activeDevice?.os_name || "Not detected"}</Typography>
              <Typography variant="body2"><strong>Stage:</strong> {stageBadge(activeDevice?.scan_stage).label}</Typography>
              {Array.isArray(activeDevice?.open_ports) && activeDevice.open_ports.length ? (
                <Typography variant="body2"><strong>Open ports:</strong> {activeDevice.open_ports.join(", ")}</Typography>
              ) : null}
              {Array.isArray(activeDevice?.connector_plan) && activeDevice.connector_plan.length ? (
                <Box>
                  <Typography variant="body2"><strong>Connector plan:</strong></Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                    {activeDevice.connector_plan.map((connector) => {
                      const badge = connectorBadge(connector.status);
                      return (
                        <Chip
                          key={`${connector.connector_type}-${connector.matched_credential_id || connector.status}`}
                          size="small"
                          label={`${connector.connector_type.toUpperCase()} • ${badge.label}`}
                          sx={{ bgcolor: badge.bg, color: badge.fg, fontWeight: 700 }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}
              <Typography variant="body2"><strong>Status:</strong> {activeDevice?.is_online ? "Online" : "Offline"}</Typography>
              {activeDevice?.monitor_device_key ? (
                <Typography variant="body2"><strong>Monitor key:</strong> {activeDevice.monitor_device_key}</Typography>
              ) : null}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0", bgcolor: "white", flex: 1, overflow: "auto" }}>
            <Stack spacing={1.5}>
              <TextField label="Device name" value={form.display_name} onChange={(e) => setForm((prev) => ({ ...prev, display_name: e.target.value }))} helperText="Friendly name shown in Monitor." />
              <TextField label="Assigned owner / user" value={form.assigned_to} onChange={(e) => setForm((prev) => ({ ...prev, assigned_to: e.target.value }))} helperText="Optional, can be completed later." />
              <TextField label="Department" value={form.department} onChange={(e) => setForm((prev) => ({ ...prev, department: e.target.value }))} />
              <TextField label="Location" value={form.location} onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))} />
              <TextField label="Serial number" value={form.serial_number} onChange={(e) => setForm((prev) => ({ ...prev, serial_number: e.target.value }))} />
              <TextField label="Notes" multiline minRows={3} value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} />
              <Alert severity="info">
                Save now to create or update the Monitor device record. Additional details can be completed later from Monitor.
              </Alert>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button onClick={() => setDrawerOpen(false)} color="inherit">
              Close
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => void saveDrawer()}
              disabled={saving}
            >
              {saving ? "Saving..." : drawerMode === "edit" ? "Save device" : "Sync to Monitor"}
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Dialog open={cliDialogOpen} onClose={() => setCliDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Install agentless discovery CLI</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Install this CLI on any Windows or Linux machine inside the customer network. It scans the local subnet and sends the discovered devices back to this workspace.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc" }}>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Download files
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button component="a" href="/agents/agentless-scan-cli.mjs" download variant="outlined">
                    Download Node CLI
                  </Button>
                  <Button component="a" href="/agents/agentless-scan-cli.cmd" download variant="outlined">
                    Download Windows wrapper
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API URL"
                  value={cliApiUrl}
                  InputProps={{ readOnly: true }}
                  helperText="The CLI will post scan results to this backend URL."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Agent token"
                  value={tokenLoading ? "Loading..." : agentToken || "No token found"}
                  InputProps={{ readOnly: true }}
                  helperText="Copy this token into the CLI command."
                />
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Windows
              </Typography>
              <TextField fullWidth multiline minRows={2} value={cliWindowsCommand} InputProps={{ readOnly: true }} />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                macOS / Linux
              </Typography>
              <TextField fullWidth multiline minRows={2} value={cliCommand} InputProps={{ readOnly: true }} />
            </Paper>

            <Alert severity="warning">
              The CLI must run on a machine that can reach the target subnet directly or through VPN.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={() => void copyCliCommand()}>
            Copy command
          </Button>
          <Button onClick={() => setCliDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PremiumFeatureGate>
  );
}
